import { Inject, Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import {
  AV_TELEMETRY_BUFFER_CONFIG,
  AV_TELEMETRY_MAX_EXPECTED_ROW_BYTES,
  AvTelemetryBufferConfig,
  AvTelemetryBufferRow,
} from '../models/av-telemetry-buffer.model';
import { AvTelemetryEnvelope } from '../models/av-telemetry.model';
import { AvTelemetryBufferStore } from './av-telemetry-buffer.store';
import { AvTelemetrySamplerService } from './av-telemetry-sampler.service';
import { collapseTicks } from './av-telemetry-tick-collapse';

@Injectable({ providedIn: 'root' })
export class AvTelemetryBufferService {
  private readonly flushed = new Subject<readonly AvTelemetryEnvelope[]>();

  readonly flushed$: Observable<readonly AvTelemetryEnvelope[]> = this.flushed.asObservable();

  private sampler: AvTelemetrySamplerService | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private deviceCode = '';

  constructor(
    private readonly bufferStore: AvTelemetryBufferStore,
    @Inject(AV_TELEMETRY_BUFFER_CONFIG) private readonly config: AvTelemetryBufferConfig
  ) {}

  start(sampler: AvTelemetrySamplerService, deviceCode: string): void {
    if (this.intervalId !== null) {
      return;
    }

    this.sampler = sampler;
    this.deviceCode = deviceCode ?? '';

    this.intervalId = setInterval(() => void this.flush(), this.config.flushIntervalMs);
  }

  async stop(): Promise<void> {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.sampler) {
      await this.flush();
      this.sampler = null;
    }
  }

  async nextSequence(deviceCode: string): Promise<number> {
    const rows = await this.bufferStore.readAll();

    return rows
      .filter((row) => row.deviceCode === (deviceCode ?? ''))
      .reduce((next, row) => Math.max(next, row.envelope.seq + 1), 0);
  }

  async flush(): Promise<void> {
    const drained = this.sampler?.drain() ?? [];

    await this.purgeExpired();

    if (!drained.length) {
      return;
    }

    await this.bufferStore.put(this.toRows(drained));
    await this.enforceCap();

    this.flushed.next(drained);
  }

  private toRows(envelopes: readonly AvTelemetryEnvelope[]): AvTelemetryBufferRow[] {
    const expiresAt = Date.now() + this.config.ttlMs;

    return envelopes.map((envelope) => ({
      deviceCode: this.deviceCode,
      expiresAt: expiresAt,
      bytes: JSON.stringify(envelope).length,
      envelope: envelope,
    }));
  }

  private async purgeExpired(): Promise<void> {
    const expired = await this.bufferStore.readExpired(Date.now());
    await this.bufferStore.deleteByIds(expired.map((row) => row.id));
  }

  private async enforceCap(): Promise<void> {
    const gate = this.config.maxBytes / AV_TELEMETRY_MAX_EXPECTED_ROW_BYTES;
    if ((await this.bufferStore.count()) < gate) {
      return;
    }

    if ((await this.totalBytes()) <= this.config.maxBytes) {
      return;
    }

    await this.collapseOldestTicks();

    const overBy = (await this.totalBytes()) - this.config.maxBytes;
    if (overBy > 0) {
      await this.shedOldestAggregates(overBy);
    }
  }

  private async totalBytes(): Promise<number> {
    const rows = await this.bufferStore.readAll();

    return rows.reduce((sum, row) => sum + (row.bytes ?? 0), 0);
  }

  private async collapseOldestTicks(): Promise<void> {
    const ticks = await this.bufferStore.readByType('tick');
    if (!ticks.length) {
      return;
    }

    const aggregates = collapseTicks(
      ticks.map((row) => row.envelope),
      this.config.collapseWindowMs
    );
    if (!aggregates.length) {
      return;
    }

    await this.bufferStore.put(this.toRows(aggregates));
    await this.bufferStore.deleteByIds(ticks.map((row) => row.id));
  }

  private async shedOldestAggregates(overByBytes: number): Promise<void> {
    const aggregates = await this.bufferStore.readByType('tickAggregate');
    const doomed: number[] = [];
    let freed = 0;

    for (const row of aggregates) {
      if (freed >= overByBytes) {
        break;
      }

      doomed.push(row.id);
      freed += row.bytes ?? 0;
    }

    await this.bufferStore.deleteByIds(doomed);
  }
}
